import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export default function IntroOverlay() {
  const { t } = useLang();
  const [show, setShow] = useState(true);

  useEffect(() => {
    // Lock scroll during the intro. Lenis stops preventDefault when stopped,
    // so we block wheel/touch/keys ourselves for a reliable lock.
    const scrollKeys = [" ", "PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End"];
    const prevent = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    const keyBlock = (e) => {
      if (scrollKeys.includes(e.key)) prevent(e);
    };
    const opts = { passive: false, capture: true };
    const lock = () => {
      window.scrollTo(0, 0);
      window.__lenis?.stop();
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      window.addEventListener("wheel", prevent, opts);
      window.addEventListener("touchmove", prevent, opts);
      window.addEventListener("keydown", keyBlock, opts);
    };
    const unlock = () => {
      window.removeEventListener("wheel", prevent, opts);
      window.removeEventListener("touchmove", prevent, opts);
      window.removeEventListener("keydown", keyBlock, opts);
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.__lenis?.start();
    };

    lock();
    const reassert = setTimeout(lock, 100);
    const timer = setTimeout(() => {
      setShow(false);
      unlock();
      // Guarantee the hero is revealed from the top.
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        window.__lenis?.scrollTo(0, { immediate: true, force: true });
      });
    }, 2200);
    return () => {
      clearTimeout(timer);
      clearTimeout(reassert);
      unlock();
    };
  }, []);

  const words = t("intro").split(" ");

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="intro-overlay"
          className="fixed inset-0 z-[10000] flex items-center justify-center overflow-hidden bg-[#1A2118]"
          exit={{ y: "-100%" }}
          transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
        >
          {/* Flowing organic blobs */}
          <motion.div
            aria-hidden
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.2, opacity: 0.5 }}
            transition={{ duration: 2.2, ease: "easeInOut" }}
            className="pointer-events-none absolute -left-32 -top-32 h-[36rem] w-[36rem] rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, #4A5D4E 0%, transparent 70%)" }}
          />
          <motion.div
            aria-hidden
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.3, opacity: 0.4 }}
            transition={{ duration: 2.2, ease: "easeInOut", delay: 0.2 }}
            className="pointer-events-none absolute -bottom-40 -right-24 h-[34rem] w-[34rem] rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, #B85D43 0%, transparent 70%)" }}
          />

          {/* Brand mark + tagline reveal */}
          <div className="relative z-10 px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mb-6 flex items-center justify-center gap-3"
            >
              <Leaf strokeWidth={1.5} className="h-8 w-8 text-[#B85D43]" />
              <span className="font-serif-display text-3xl font-bold tracking-tight text-[#F4F1EB]">
                Herb Labs
              </span>
            </motion.div>

            <h1 className="font-serif-display text-3xl font-light text-[#F4F1EB] md:text-5xl">
              {words.map((w, i) => (
                <span key={i} className="mask-line mx-1.5 inline-block align-bottom">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.09 }}
                    className="inline-block"
                  >
                    {w}
                  </motion.span>
                </span>
              ))}
            </h1>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
